import type {
    DailyConcentrationDto,
    DailyExecutivePeriodDto,
    DailyExecutiveRiskItemDto,
    DailyExecutiveSummaryDto,
    DailyGrowthRowDto,
    DailyRecommendedActionDto,
    DailyRiskSeverity,
    MetricDto,
    MoneyByCurrencyDto,
} from "@/features/daily/types/daily.types";
import { DailyTone, commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";
import type {
    ActionVm,
    GrowthRowVm,
    KpiGroupVm,
    RiskVm,
    SeverityVm,
} from "@/features/daily/components/executive/legacy-view-model";
import { DeltaVm, statTileViewModel } from "@/features/daily/components/stat-tile-view-model";
import { moneyByType } from "@/features/daily/components/money-by-type";

/**
 * D111 + B8: maps the backend's executive summary into the legacy layout. Every
 * figure, change, risk and action comes from the server; this file only formats
 * them and says which direction is good news. Money is always ONE currency.
 */
type GrowthKey = DailyGrowthRowDto["key"];

const GROWTH_TEXT: Record<GrowthKey, string> = {
    volume: "Volumen",
    revenue: "Ingresos",
    completed_operations: "Operaciones completadas",
    average_ticket: "Ticket medio",
    take_rate: "Take rate",
    success_rate: "Tasa de éxito",
};

const SEVERITY_TEXT: Record<DailyRiskSeverity, SeverityVm> = {
    critical: { label: "Crítica", tone: "critical" },
    high: { label: "Alta", tone: "warning" },
    medium: { label: "Media", tone: "warning" },
    low: { label: "Baja", tone: "info" },
};

/** D115: what each KPI measures, for its tooltip. */
const DEFINITION: Record<string, string> = {
    volume: "Dinero recibido en el periodo: depósitos y órdenes no pagadas con saldo, ni pendientes ni canceladas, fechados por la llegada del dinero, en la moneda seleccionada.",
    orders: "Órdenes completadas con éxito en el periodo, todas las monedas.",
    operations: "Operaciones completadas con éxito en el periodo (órdenes y depósitos), todas las monedas.",
    revenue: "Comisiones de todas las operaciones con éxito (órdenes y depósitos), en la moneda seleccionada.",
    "take-rate": "Ingresos divididos por el volumen, en la misma moneda.",
    success: "Conversión: operaciones con fondos recibidos sobre creadas · sin canceladas",
    p95: "El 95 % de las completadas tardó como mucho esto, desde la recepción de fondos.",
    slow: "Completadas que superaron el SLA de extremo a extremo.",
    concentration: "Parte de los ingresos de la moneda que aporta el origen principal.",
};

const IMPACT_TEXT: Record<DailyRecommendedActionDto["impact"], SeverityVm> = {
    high: { label: "Alto", tone: "critical" },
    medium: { label: "Medio", tone: "warning" },
    low: { label: "Bajo", tone: "info" },
};


const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the cache stores `unknown`; the page proves the shape instead of casting. */
export const isExecutiveSummaryDto = (value: unknown): value is DailyExecutiveSummaryDto =>
    isRecord(value)
    && isRecord(value.envelope)
    && isRecord(value.targets)
    && isRecord(value.current)
    && isRecord(value.previous)
    && Array.isArray(value.growth)
    && Array.isArray(value.risks)
    && Array.isArray(value.actions);

const keyOf = (currency: string | null): string => currency ?? "";

const directionOf = (value: number): DeltaVm["direction"] => (value > 0 ? "up" : value < 0 ? "down" : "flat");

/** A change the server computed; `upIsGood` says which way is good news. */
const deltaOf = (change: number | null, unit: "%" | " pp", upIsGood: boolean): DeltaVm | null =>
    change === null
        ? null
        : { label: dailyFormat.kpiDelta(change, unit), direction: directionOf(change), good: change === 0 ? null : (change > 0) === upIsGood };

const takeRateLabel = (value: number | null): string => dailyFormat.kpiPercent(value, 2);

type OperationsCount = { value: number | null; label: string; fullLabel: string; unit: string; breakdown: string | null };

/**
 * D139: the operations tile counts every successful operation (orders + deposits) and
 * names what it counts — «Operaciones» when both types are there, else the one present.
 * Without the per-type read it falls back to the orders count.
 */
const operationsOf = (period: DailyExecutivePeriodDto): OperationsCount => {
    const byType = period.completedOperationsByType;
    if (byType === null) {
        return { value: period.completedOperations.value, label: "Órdenes", fullLabel: "Órdenes completadas", unit: "órdenes completadas", breakdown: null };
    }
    const { order, deposit } = byType.byType;
    if (moneyByType.bothPresent(order, deposit)) {
        return {
            value: byType.total,
            label: "Operaciones",
            fullLabel: "Operaciones completadas",
            unit: "operaciones completadas",
            breakdown: `Órdenes ${dailyFormat.exactCount(order)} · Depósitos ${dailyFormat.exactCount(deposit)}`,
        };
    }
    return deposit !== 0
        ? { value: byType.total, label: "Depósitos", fullLabel: "Depósitos completados", unit: "depósitos completados", breakdown: null }
        : { value: byType.total, label: "Órdenes", fullLabel: "Órdenes completadas", unit: "órdenes completadas", breakdown: null };
};

/** D142: a target duration the way the legacy strip wrote it — «SLA: 4 h», «SLA: 2.50 h», «SLA: 30 min». */
const targetDuration = (ms: number): string =>
    ms >= 3_600_000 && ms % 3_600_000 === 0 ? `${ms / 3_600_000} h` : dailyFormat.kpiDuration(ms);

const ARROWS_LEGEND = "▲ mejora · ▼ empeora · — sin cambio o sin base; el color sigue al significado (un P95 que sube empeora).";

const changePct = (now: number | null, before: number | null): number | null =>
    now === null || before === null || before === 0 ? null : ((now - before) / before) * 100;

const moneyOf = (rows: MoneyByCurrencyDto[], currency: string): number | null => {
    const row = rows.find((entry) => keyOf(entry.currency) === currency);
    return row === undefined ? null : row.amount;
};

const growthRow = (dto: DailyExecutiveSummaryDto, key: GrowthKey, currency: string | null): DailyGrowthRowDto | undefined =>
    dto.growth.find((row) => row.key === key && (currency === null || keyOf(row.currency) === currency));

const valueFor = (key: GrowthKey, value: number | null, currency: string | null): string => {
    if (value === null) return dailyFormat.absent;
    if (key === "volume" || key === "revenue" || key === "average_ticket") return dailyFormat.compactMoney(value, currency);
    if (key === "take_rate") return takeRateLabel(value);
    if (key === "success_rate") return dailyFormat.kpiPercent(value, 2);
    return dailyFormat.compactCount(value);
};

const metricReasons = (...metrics: MetricDto[]): string[] =>
    Array.from(new Set(metrics.flatMap((metric) => commandViewModel.reasonTexts(metric.reasonCodes))));

/** Which currency the page opens on: the one that moved the most volume. */
const topCurrency = (period: DailyExecutivePeriodDto): string => {
    const top = [...period.completedVolume].sort((left, right) => right.amount - left.amount)[0];
    return top === undefined ? "" : keyOf(top.currency);
};

const concentrationOf = (period: DailyExecutivePeriodDto, currency: string): DailyConcentrationDto | undefined =>
    period.concentration.find((entry) => keyOf(entry.currency) === currency);

/** The top origin: the account when the server names one, else the workspace. */
const topShare = (entry: DailyConcentrationDto | undefined): { who: string; share: number } | null => {
    if (entry === undefined) return null;
    // Guardian P2: the server's label, never a raw account or workspace id (it can be a UUID).
    if (entry.topAccount !== null) return { who: entry.topAccountLabel ?? "una cuenta sin etiqueta", share: entry.topAccount.share };
    if (entry.topWorkspace !== null) return { who: entry.topWorkspaceLabel ?? "un espacio de trabajo sin etiqueta", share: entry.topWorkspace.share };
    return null;
};

// B9: how each risk's `current` and `threshold` are expressed. Ids are the server's
// catalogue; an id this map does not know is printed as a plain count.
const DURATION_RISKS = ["p95_completion_over_target", "funding_latency"];
const RATIO_RISKS = ["slow_orders", "commercial_conversion", "operational_success", "success_rate_below_target", "slow_rate_over_target"];
const RATIO_PREFIXES = ["revenue_concentration:", "concentration_"];

const riskCurrent = (risk: DailyExecutiveRiskItemDto): string => {
    const ratio = RATIO_RISKS.includes(risk.id) || RATIO_PREFIXES.some((prefix) => risk.id.startsWith(prefix));
    const format = (value: number | null): string => {
        if (value === null) return dailyFormat.absent;
        if (DURATION_RISKS.includes(risk.id)) return dailyFormat.duration(value);
        return ratio ? dailyFormat.ratioPercent(value) : dailyFormat.count(value);
    };
    const current = format(risk.current);
    return risk.threshold === null ? current : `${current} · objetivo ${format(risk.threshold)}`;
};

export const executiveSummaryViewModel = {
    topCurrency: (dto: DailyExecutiveSummaryDto): string => topCurrency(dto.current),

    currencies: (dto: DailyExecutiveSummaryDto): { key: string; label: string }[] =>
        dto.current.completedVolume.map((row) => ({ key: keyOf(row.currency), label: row.currency ?? "Moneda no registrada" })),

    /** D143: the arrows' legend moves into every tile's tooltip. */
    groups: (dto: DailyExecutiveSummaryDto, currencyKey: string): KpiGroupVm[] =>
        executiveSummaryViewModel.baseGroups(dto, currencyKey).map((group) => ({
            ...group,
            cards: group.cards.map((card) => ({ ...card, tooltip: [...card.tooltip, ARROWS_LEGEND] })),
        })),

    baseGroups: (dto: DailyExecutiveSummaryDto, currencyKey: string): KpiGroupVm[] => {
        const currency = currencyKey === "" ? null : currencyKey;
        const { current, previous, targets } = dto;
        const volume = growthRow(dto, "volume", currencyKey);
        const revenue = growthRow(dto, "revenue", currencyKey);
        const operationsNow = operationsOf(current);
        const operationsBefore = operationsOf(previous);
        const takeRate = growthRow(dto, "take_rate", currencyKey);
        const success = growthRow(dto, "success_rate", null);
        const p95Now = current.p95Completion.value;
        const p95Before = previous.p95Completion.value;
        const slowNow = current.slowRate.value;
        const slowBefore = previous.slowRate.value;
        const topNow = topShare(concentrationOf(current, currencyKey));
        const topBefore = topShare(concentrationOf(previous, currencyKey));
        const concentrationEntry = concentrationOf(current, currencyKey);
        const p95Breach = p95Now !== null && p95Now > targets.p95CompletionMs;
        const concentrationBreach = topNow !== null && topNow.share > targets.concentrationMaxShare;

        const reasonsOf = (...metrics: MetricDto[]): string[] => metricReasons(...metrics);
        const money = (amount: number | null) => ({
            value: dailyFormat.compactMoney(amount, currency),
            valueExact: dailyFormat.exactMoney(amount, currency),
        });
        const volumeNow = moneyOf(current.completedVolume, currencyKey);
        const volumeBefore = moneyOf(previous.completedVolume, currencyKey);
        const revenueNow = moneyOf(current.revenue, currencyKey);
        const revenueBefore = moneyOf(previous.revenue, currencyKey);
        const volumeRowOf = (period: DailyExecutivePeriodDto) => period.completedVolume.find((row) => keyOf(row.currency) === currencyKey);
        const revenueRowOf = (period: DailyExecutivePeriodDto) => period.revenue.find((row) => keyOf(row.currency) === currencyKey);
        const takeNow = takeRate === undefined ? null : takeRate.current;
        const takeBefore = takeRate === undefined ? null : takeRate.previous;
        const before = (text: string): string => `Periodo anterior: ${text}`;
        // D123: the funded-operational rate stays as a secondary figure, never the tile's value.
        const fundedLine = (metric: MetricDto | undefined): string[] =>
            metric === undefined ? [] : [`Sobre financiadas: ${dailyFormat.kpiPercent(metric.value, 2)}`];
        const tile = statTileViewModel.tile;

        return [
            {
                key: "growth",
                label: "CRECIMIENTO",
                cards: [
                    tile({
                        key: "volume",
                        label: "Volumen",
                        fullLabel: "Volumen transaccionado",
                        ...money(volumeNow),
                        reference: `Anterior: ${dailyFormat.compactMoney(volumeBefore, currency)}`,
                        delta: deltaOf(volume === undefined ? null : volume.change, "%", true),
                        tooltip: [DEFINITION.volume, ...moneyByType.volumeLines(volumeRowOf(current)), before(dailyFormat.exactMoney(volumeBefore, currency))],
                    }),
                    tile({
                        key: "orders",
                        label: operationsNow.label,
                        fullLabel: operationsNow.fullLabel,
                        value: dailyFormat.compactCount(operationsNow.value),
                        valueExact: `${dailyFormat.exactCount(operationsNow.value)} ${operationsNow.unit}`,
                        reference: `Anterior: ${dailyFormat.compactCount(operationsBefore.value)}`,
                        delta: deltaOf(changePct(operationsNow.value, operationsBefore.value), "%", true),
                        tooltip: [
                            current.completedOperationsByType === null ? DEFINITION.orders : DEFINITION.operations,
                            ...(operationsNow.breakdown === null ? [] : [operationsNow.breakdown]),
                            before(dailyFormat.exactCount(operationsBefore.value)),
                            ...reasonsOf(current.completedOperations),
                        ],
                    }),
                ],
            },
            {
                key: "monetization",
                label: "MONETIZACIÓN",
                cards: [
                    tile({
                        key: "revenue",
                        label: "Ingresos",
                        ...money(revenueNow),
                        reference: `Anterior: ${dailyFormat.compactMoney(revenueBefore, currency)}`,
                        delta: deltaOf(revenue === undefined ? null : revenue.change, "%", true),
                        tooltip: [DEFINITION.revenue, ...moneyByType.revenueLines(revenueRowOf(current)), before(dailyFormat.exactMoney(revenueBefore, currency))],
                    }),
                    tile({
                        key: "take-rate",
                        label: "Take rate",
                        value: takeRateLabel(takeNow),
                        valueExact: `Take rate ${takeRateLabel(takeNow)}`,
                        reference: `Anterior: ${takeRateLabel(takeBefore)}`,
                        delta: deltaOf(takeRate === undefined ? null : takeRate.change, " pp", true),
                        tooltip: [DEFINITION["take-rate"], before(takeRateLabel(takeBefore))],
                    }),
                ],
            },
            {
                key: "quality",
                label: "CALIDAD",
                cards: [
                    tile({
                        key: "success",
                        label: "Tasa de éxito",
                        value: dailyFormat.kpiPercent(current.successRate.value, 2),
                        valueExact: `Tasa de éxito ${dailyFormat.kpiPercent(current.successRate.value, 2)}`,
                        reference: `Objetivo: ${dailyFormat.kpiPercent(targets.successRate, 2)}`,
                        delta: deltaOf(success === undefined ? null : success.change, " pp", true),
                        tooltip: [DEFINITION.success, ...fundedLine(current.fundedSuccessRate), before(dailyFormat.kpiPercent(previous.successRate.value, 2)), `Objetivo: ${dailyFormat.kpiPercent(targets.successRate, 2)}`, ...reasonsOf(current.successRate)],
                    }),
                    tile({
                        key: "p95",
                        label: "P95",
                        fullLabel: "P95 de finalización",
                        value: dailyFormat.kpiDuration(p95Now),
                        valueExact: `P95 de finalización ${dailyFormat.kpiDuration(p95Now)}`,
                        reference: `SLA: ${targetDuration(targets.p95CompletionMs)}`,
                        // A longer P95 is bad news: the colour follows the meaning, not the sign.
                        delta: p95Now === null || p95Before === null
                            ? null
                            : { label: dailyFormat.kpiDuration(Math.abs(p95Now - p95Before)), direction: directionOf(p95Now - p95Before), good: p95Now === p95Before ? null : p95Now < p95Before },
                        flag: p95Breach ? { label: "INCUMPLE", tone: "critical" } : null,
                        border: p95Breach ? "critical" : "neutral",
                        tooltip: [DEFINITION.p95, before(dailyFormat.kpiDuration(p95Before)), `SLA: ${dailyFormat.kpiDuration(targets.p95CompletionMs)}`, ...reasonsOf(current.p95Completion)],
                    }),
                ],
            },
            {
                key: "risk",
                label: "RIESGO",
                cards: [
                    tile({
                        key: "slow",
                        label: "Órdenes lentas",
                        fullLabel: "Tasa de órdenes lentas",
                        value: dailyFormat.kpiPercent(slowNow, 2),
                        valueExact: `Tasa de órdenes lentas ${dailyFormat.kpiPercent(slowNow, 2)}`,
                        reference: `Máximo: ${dailyFormat.kpiPercent(targets.slowRateMax, 2)}`,
                        delta: deltaOf(slowNow === null || slowBefore === null ? null : (slowNow - slowBefore) * 100, " pp", false),
                        tooltip: [DEFINITION.slow, before(dailyFormat.kpiPercent(slowBefore, 2)), `Máximo: ${dailyFormat.kpiPercent(targets.slowRateMax, 2)}`, ...reasonsOf(current.slowRate)],
                    }),
                    tile({
                        key: "concentration",
                        label: "Concentración",
                        fullLabel: "Concentración de ingresos",
                        value: topNow === null ? dailyFormat.absent : dailyFormat.kpiPercent(topNow.share, 1),
                        valueExact: topNow === null ? "Concentración sin medir" : `Concentración ${dailyFormat.kpiPercent(topNow.share, 1)} en ${topNow.who}`,
                        reference: topNow === null ? `Máximo: ${dailyFormat.kpiPercent(targets.concentrationMaxShare, 1)}` : `Principal: ${topNow.who}`,
                        delta: deltaOf(topNow === null || topBefore === null ? null : (topNow.share - topBefore.share) * 100, " pp", false),
                        flag: concentrationBreach ? { label: "EXCEDE", tone: "warning" } : null,
                        border: concentrationBreach ? "warning" : "neutral",
                        tooltip: [
                            DEFINITION.concentration,
                            ...(concentrationBreach ? ["Excede el máximo de concentración"] : []),
                            before(topBefore === null ? dailyFormat.absent : dailyFormat.kpiPercent(topBefore.share, 1)),
                            ...(topNow === null ? [] : [`Principal: ${topNow.who}`]),
                            `Máximo: ${dailyFormat.kpiPercent(targets.concentrationMaxShare, 1)}`,
                            ...(concentrationEntry === undefined ? [] : commandViewModel.reasonTexts(concentrationEntry.reasonCodes)),
                        ],
                    }),
                ],
            },
        ];
    },

    growth: (dto: DailyExecutiveSummaryDto, currencyKey: string): GrowthRowVm[] =>
        dto.growth
            .filter((row) => row.currency === null || keyOf(row.currency) === currencyKey)
            .flatMap((row) => {
                if (row.key === "completed_operations") {
                    // D139: the same count as the operations tile — orders + deposits, named by what it holds.
                    const now = operationsOf(dto.current);
                    const before = operationsOf(dto.previous);
                    return [{
                        key: row.key,
                        metric: now.fullLabel,
                        secondary: false,
                        currentLabel: dailyFormat.compactCount(now.value),
                        previousLabel: dailyFormat.compactCount(before.value),
                        change: deltaOf(changePct(now.value, before.value), "%", true),
                    }];
                }
                const main: GrowthRowVm = {
                    key: row.key,
                    // D129: the currency is the page's selected one, so the label never repeats it.
                    metric: GROWTH_TEXT[row.key],
                    secondary: false,
                    currentLabel: valueFor(row.key, row.current, row.currency),
                    previousLabel: valueFor(row.key, row.previous, row.currency),
                    change: deltaOf(row.change, row.changeKind === "pct" ? "%" : " pp", true),
                };
                if (row.key !== "volume" && row.key !== "revenue") return [main];
                // D136: what volume and revenue are made of, each type against its own previous.
                const rowsOf = (period: DailyExecutivePeriodDto) => (row.key === "volume" ? period.completedVolume : period.revenue)
                    .find((entry) => keyOf(entry.currency) === currencyKey);
                const now = rowsOf(dto.current);
                const before = rowsOf(dto.previous);
                // D139: a breakdown only when both types carry an amount this period.
                if (now === undefined || !moneyByType.bothPresent(now.byType.order.amount, now.byType.deposit.amount)) return [main];
                const line = (type: "order" | "deposit", label: string): GrowthRowVm => {
                    const current = now === undefined ? null : now.byType[type].amount;
                    const previous = before === undefined ? null : before.byType[type].amount;
                    return {
                        key: `${row.key}-${type}`,
                        metric: label,
                        secondary: true,
                        currentLabel: valueFor(row.key, current, row.currency),
                        previousLabel: valueFor(row.key, previous, row.currency),
                        change: deltaOf(current === null || previous === null || previous === 0 ? null : ((current - previous) / previous) * 100, "%", true),
                    };
                };
                return [main, line("order", "Órdenes"), line("deposit", "Depósitos")];
            }),

    risks: (dto: DailyExecutiveSummaryDto): RiskVm[] =>
        dto.risks.map((risk) => ({
            id: risk.id,
            title: risk.label,
            currentLabel: riskCurrent(risk),
            severity: SEVERITY_TEXT[risk.severity],
            why: risk.evidence.length === 0 ? risk.whyItMatters : `${risk.whyItMatters} ${risk.evidence.join(" · ")}.`,
        })),

    actions: (dto: DailyExecutiveSummaryDto): ActionVm[] =>
        [...dto.actions]
            .sort((left, right) => left.rank - right.rank)
            .map((action) => ({
                id: `${action.rank}:${action.riskId}`,
                title: action.title,
                impact: IMPACT_TEXT[action.impact],
                evidence: action.evidenceChips,
                primaryImpact: action.primaryImpact,
                action: action.action,
            })),

    /** The tone of a severity chip, for places outside the lists. */
    severityTone: (severity: DailyRiskSeverity): DailyTone => SEVERITY_TEXT[severity].tone,
};
